package com.ijoin.ui.util

import com.ijoin.i18n.Strings
import com.ijoin.i18n.globalStrings
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonPrimitive

private val json = Json { ignoreUnknownKeys = true }

private data class BackendErrorMapping(
    val predicate: (String) -> Boolean,
    val resolve: (Strings) -> String,
)

private val backendErrorMappings = listOf(
    BackendErrorMapping({ it.contains("user not authenticated") }) { it.errorUserNotAuthenticated },
    BackendErrorMapping({ it.contains("access denied") }) { it.errorAccessDenied },
    BackendErrorMapping({ it.contains("following list") }) { it.errorForbiddenFollowingList },
    BackendErrorMapping({ it.contains("dataurl is required") }) { it.errorDataUrlRequired },
    BackendErrorMapping({ it.contains("failed to store media") }) { it.errorMediaStoreFailed },
    BackendErrorMapping({ it.contains("amount should be greater than zero") }) { it.errorAmountMustBePositive },
    BackendErrorMapping({ it.contains("requested amount exceeds available balance") }) { it.errorAmountExceedsBalance },
    BackendErrorMapping({ it.contains("email is required for etransfer") }) { it.errorPayoutEmailRequired },
    BackendErrorMapping({ it.contains("full name, routing number and account number") }) { it.errorPayoutWiseInfoRequired },
    BackendErrorMapping({ it.contains("unsupported account type") }) { it.errorPayoutAccountType },
    BackendErrorMapping({ it.contains("unsupported payout method") }) { it.errorUnsupportedPayoutMethod },
    BackendErrorMapping({ it.contains("event is not cancelled") }) { it.errorEventNotCancelled },
    BackendErrorMapping({ it.contains("cannot start chat with yourself") }) { it.errorChatWithSelf },
    BackendErrorMapping({ it.contains("message cannot be empty") }) { it.errorChatMessageEmpty },
    BackendErrorMapping({ it.contains("event id and organizer are required") }) { it.errorEventOrganizerRequired },
    BackendErrorMapping({ it.contains("event is no longer available") }) { it.errorEventUnavailable },
    BackendErrorMapping({ it.contains("event has already finished") }) { it.errorEventFinished },
    BackendErrorMapping({ it.contains("not a service provider") }) { it.errorOrganizerNotProvider },
    BackendErrorMapping({ it.contains("service provider is not active") }) { it.errorServiceProviderInactive },
    BackendErrorMapping({ it.contains("time slots not found") || it.contains("slots were not found") }) { it.errorSlotsNotFound },
    BackendErrorMapping({ it.contains("must belong to the same event") }) { it.errorSlotsMustSameEvent },
    BackendErrorMapping({ it.contains("must belong to the event's organizer") }) { it.errorSlotsMustSameOrganizer },
    BackendErrorMapping({ it.contains("time slots") && it.contains("not available") }) { it.errorSlotsUnavailable },
    BackendErrorMapping({ it.contains("slots that are in the past") }) { it.errorSlotsInPast },
    BackendErrorMapping({ it.contains("must be consecutive") }) { it.errorSlotsMustBeConsecutive },
    BackendErrorMapping({ it.contains("event id is required") }) { it.errorEventIdRequired },
    BackendErrorMapping({ it.contains("quantity must be at least 1") }) { it.errorQuantityAtLeastOne },
    BackendErrorMapping({ it.contains("paid reservation") }) { it.errorPaidReservationRequired },
    BackendErrorMapping({ it.contains("organizer is not valid") }) { it.errorEventOrganizerInvalid },
    BackendErrorMapping({ it.contains("organizer is not set") }) { it.errorEventOrganizerMissing },
    BackendErrorMapping({ it.contains("not able to accept bookings") }) { it.errorOrganizerCannotAccept },
    BackendErrorMapping({ it.contains("occurrence date is required") }) { it.errorOccurrenceRequired },
    BackendErrorMapping({ it.contains("selected occurrence has already passed") }) { it.errorOccurrencePassed },
    BackendErrorMapping({ it.contains("not enough tickets available") }) { it.errorTicketsNotEnough },
    BackendErrorMapping({ it.contains("booking belongs to another user") }) { it.errorBookingOtherUser },
    BackendErrorMapping({ it.contains("booking is not awaiting payment") }) { it.errorBookingNotAwaitingPayment },
    BackendErrorMapping({ it.contains("booking is already cancelled") }) { it.errorBookingCancelled },
    BackendErrorMapping({ it.contains("booking is no longer active") }) { it.errorBookingNotActive },
    BackendErrorMapping({ it.contains("booking already has issued tickets") }) { it.errorBookingIssuedTickets },
    BackendErrorMapping({ it.contains("requires payment, use payment flow") }) { it.errorEventRequiresPaymentFlow },
    BackendErrorMapping({ it.contains("quantity exceeds booking reservation") || it.contains("quantity exceeds reservation") }) {
        it.errorReservationQuantityExceeded
    },
    BackendErrorMapping({ it.contains("ticket already validated") }) { it.errorTicketAlreadyValidated },
    BackendErrorMapping({ it.contains("ticket is not active") }) { it.errorTicketInactive },
    BackendErrorMapping({ it.contains("ticket already cancelled") }) { it.errorTicketAlreadyCancelled },
    BackendErrorMapping({ it.contains("used tickets cannot be cancelled") }) { it.errorTicketUsed },
    BackendErrorMapping({ it.contains("occurrences are required") }) { it.errorOccurrencesRequired },
    BackendErrorMapping({ it.contains("request is required") }) { it.errorRequestRequired },
    BackendErrorMapping({
        (it.contains("does not belong to the provided event") || it.contains("does not belong to this event")) &&
            it.contains("booking")
    }) { it.errorBookingEventMismatch },
    BackendErrorMapping({ it.contains("booking is already processed") }) { it.errorBookingProcessed },
    BackendErrorMapping({ it.contains("override amount is allowed only for administrators") }) { it.errorOverrideAmountAdminOnly },
    BackendErrorMapping({ it.contains("override amount must be positive") }) { it.errorOverrideAmountPositive },
    BackendErrorMapping({ it.contains("unable to determine ticket price") }) { it.errorUnableDetermineTicketPrice },
    BackendErrorMapping({ it.contains("email already registered") }) { it.errorEmailAlreadyRegistered },
    BackendErrorMapping({ it.contains("invalid email or password") }) { it.errorInvalidCredentials },
    BackendErrorMapping({ it.contains("account is inactive") }) { it.errorAccountInactive },
    BackendErrorMapping({ it.contains("account has been deleted") }) { it.errorAccountDeleted },
    BackendErrorMapping({ it.contains("withdraw remaining balance") && it.contains("payout") }) { it.errorAccountBalanceNotZero },
    BackendErrorMapping({ it.contains("invalid refresh token") }) { it.errorInvalidRefreshToken },
    BackendErrorMapping({ it.contains("avatar data must be provided") }) { it.errorAvatarDataUrlRequired },
    BackendErrorMapping({ it.contains("avatar must be an image") }) { it.errorAvatarMustBeImage },
    BackendErrorMapping({ it.contains("avatar data url") && (it.contains("invalid") || it.contains("missing comma") || it.contains("base64")) }) {
        it.errorAvatarInvalidDataUrl
    },
    BackendErrorMapping({ it.contains("invalid stripe signature") }) { it.errorStripeSignatureInvalid },
    BackendErrorMapping({ it.contains("unsupported image format") }) { it.errorMediaUnsupportedFormat },
    BackendErrorMapping({ it.contains("reservation expired") }) { it.errorReservationExpired },
)

fun userFriendlyErrorMessage(
    error: Throwable?,
    fallback: String = globalStrings().unknownErrorMessage,
): String = userFriendlyErrorMessage(error?.message, fallback)

fun userFriendlyErrorMessage(
    rawMessage: String?,
    fallback: String = globalStrings().unknownErrorMessage,
): String {
    val strings = globalStrings()
    val extracted = extractBackendError(rawMessage)?.ifBlank { null }
    val normalized = extracted?.lowercase()
    val mapped = normalized?.let { value ->
        backendErrorMappings.firstOrNull { mapping -> mapping.predicate(value) }?.resolve?.invoke(strings)
    }
    return mapped ?: extracted ?: fallback
}

private fun extractBackendError(raw: String?): String? {
    if (raw == null) return null
    val trimmed = raw.trim().trim('"')
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
        runCatching {
            json.parseToJsonElement(trimmed)
                .let { it as? JsonObject }
                ?.get("error")
                ?.jsonPrimitive
                ?.content
        }.getOrNull()?.let { return it }
    }
    return trimmed
}
